/**
 * O que faz: recarrega a página uma vez quando um arquivo JS antigo some após um deploy.
 * Onde usa: entrada do app (main.tsx) e carregamento preguiçoso do Chat/Pipeline.
 * Conexões: evento vite:preloadError, sessionStorage e React.lazy.
 * Tabelas: nenhuma.
 * Data: 28/08/2026
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const CHAVE_RECARGA_CHUNK = 'gens-crm-recarga-apos-chunk';

type ArmazenamentoSessao = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface RecarregarPaginaSeChunkAntigoOpcoes {
  armazenamento?: ArmazenamentoSessao;
  recarregar?: () => void;
}

export function jaTentouRecarregarPorChunk(armazenamento: ArmazenamentoSessao = sessionStorage): boolean {
  return armazenamento.getItem(CHAVE_RECARGA_CHUNK) === '1';
}

export function limparMarcacaoDeRecargaDeChunk(armazenamento: ArmazenamentoSessao = sessionStorage): void {
  armazenamento.removeItem(CHAVE_RECARGA_CHUNK);
}

export function recarregarPaginaSeChunkAntigo({
  armazenamento = sessionStorage,
  recarregar = () => window.location.reload(),
}: RecarregarPaginaSeChunkAntigoOpcoes = {}): boolean {
  // Motivo: se o arquivo novo também falhar, recarregar em loop trava o navegador.
  // Consequência: na segunda falha na mesma aba, deixamos o erro aparecer na tela.
  if (jaTentouRecarregarPorChunk(armazenamento)) {
    return false;
  }

  armazenamento.setItem(CHAVE_RECARGA_CHUNK, '1');
  recarregar();
  return true;
}

export function registrarRecargaAutomaticaDeChunk(): void {
  window.addEventListener('vite:preloadError', (evento) => {
    // Motivo: o Vite dispara este evento quando um import dinâmico (ex.: Chat) retorna 404 após deploy.
    evento.preventDefault();
    recarregarPaginaSeChunkAntigo();
  });
}

export function carregarModuloComRetry<T extends { default: ComponentType }>(
  importar: () => Promise<T>,
): LazyExoticComponent<T['default']> {
  return lazy(async () => {
    try {
      const modulo = await importar();
      limparMarcacaoDeRecargaDeChunk();
      return modulo;
    } catch (erro) {
      if (recarregarPaginaSeChunkAntigo()) {
        return { default: (() => null) as T['default'] };
      }
      throw erro;
    }
  });
}
